'use client';

import { useLocale } from '@/lib/i18n/use-locale';
import { getCsrfHeaders } from '@/lib/utils/csrf';
import { useState } from 'react';
import { ThemeToggle } from './theme-toggle';

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

type NavBarClientProps = {
  initialUser?: User | null;
};

export function NavBarClient({ initialUser = null }: NavBarClientProps) {
  const [user, setUser] = useState<User | null>(initialUser);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { locale, setLocale, t } = useLocale();

  const handleLogout = async () => {
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getCsrfHeaders()),
        },
      });

      if (response.ok || response.redirected) {
        setUser(null);
        window.location.href = '/';
      } else {
        console.error('Logout failed:', await response.text());
        setUser(null);
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout error:', error);
      setUser(null);
      window.location.href = '/';
    }
  };

  return (
    <>
      <nav className='sticky top-0 z-50 bg-white dark:bg-gray-900 shadow-sm transition-colors min-h-[72px]'>
        <div className='max-w-6xl mx-auto px-4 py-4'>
          <div className='flex justify-between items-center'>
            <a href='/' className='inline-flex items-center gap-2'>
              <span className='sr-only'>byt.la</span>
              <img
                src='/bytla_logo.svg'
                alt={t('nav.logoAlt')}
                className='h-8 md:h-9 w-auto dark:hidden'
              />
              <img
                src='/bytla_logo_dark.svg'
                alt={t('nav.logoAlt')}
                className='hidden h-8 md:h-9 w-auto dark:block'
              />
            </a>

            <div className='hidden md:flex items-center space-x-1 text-sm'>
              <a
                href='/'
                className='px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800'
              >
                {t('footer.home')}
              </a>
              <a
                href='/about'
                className='px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800'
              >
                {t('footer.about')}
              </a>
              <a
                href='/faq'
                className='px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800'
              >
                {t('footer.faq')}
              </a>
            </div>

            <div className='flex items-center space-x-3'>
              <div className='hidden md:flex items-center space-x-3'>
                {user ? (
                  <>
                    {user.role === 'admin' && (
                    <a
                      href='/admin'
                      className='px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition'
                    >
                      {t('nav.admin')}
                    </a>
                    )}
                    <a
                      href='/dashboard'
                      className='px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition'
                    >
                      {t('nav.dashboard')}
                    </a>
                    <a
                      href='/rotations'
                      className='px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition'
                    >
                      {t('rotator.title')}
                    </a>
                    <a
                      href='/settings'
                      className='px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition'
                    >
                      {t('dashboard.settings')}
                    </a>
                    <button
                      onClick={handleLogout}
                      className='px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition'
                    >
                      {t('nav.signOut')}
                    </button>
                  </>
                ) : (
                  <>
                    <a
                      href='/login'
                      className='px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition'
                    >
                      {t('nav.signIn')}
                    </a>
                  </>
                )}
              </div>
              <select
                aria-label={t('nav.language')}
                value={locale}
                onChange={e => setLocale(e.target.value as 'en' | 'th' | 'id')}
                className='hidden md:block h-9 px-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300'
              >
                <option value='en'>English</option>
                <option value='th'>ไทย</option>
                <option value='id'>Bahasa</option>
              </select>
              <ThemeToggle />
              <button
                type='button'
                className='md:hidden inline-flex items-center justify-center rounded-lg border border-gray-200 p-2 text-gray-600 hover:text-gray-900 dark:border-gray-700 dark:text-gray-300 dark:hover:text-white'
                aria-label='Toggle menu'
                onClick={() => setMobileOpen(open => !open)}
              >
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  {mobileOpen ? (
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  ) : (
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M4 6h16M4 12h16M4 18h16'
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Drawer */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className='absolute inset-0 bg-black/50' onClick={() => setMobileOpen(false)} />
        <div
          className={`absolute top-0 left-0 bottom-0 w-64 bg-white dark:bg-gray-900 shadow-xl transition-transform duration-300 ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className='flex flex-col h-full'>
            <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700'>
              <span className='text-lg font-semibold text-gray-900 dark:text-white'>Menu</span>
              <button
                onClick={() => setMobileOpen(false)}
                className='p-2 rounded-lg text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
                aria-label={t('nav.closeMenu')}
              >
                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>
            <div className='flex-1 overflow-y-auto p-4'>
              <div className='flex flex-col gap-3'>
                <a
                  href='/'
                  className='px-4 py-2 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg'
                >
                  {t('footer.home')}
                </a>
                <a
                  href='/about'
                  className='px-4 py-2 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg'
                >
                  {t('footer.about')}
                </a>
                <a
                  href='/faq'
                  className='px-4 py-2 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg'
                >
                  {t('footer.faq')}
                </a>
                <div className='border-t border-gray-200 dark:border-gray-700 my-2'></div>
                {user ? (
                  <>
                    {user.role === 'admin' && (
                      <a
                        href='/admin'
                        className='px-4 py-2 font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg'
                      >
                        {t('nav.admin')}
                      </a>
                    )}
                    <a
                      href='/dashboard'
                      className='px-4 py-2 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg'
                    >
                      {t('nav.dashboard')}
                    </a>
                    <a
                      href='/rotations'
                      className='px-4 py-2 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg'
                    >
                      {t('rotator.title')}
                    </a>
                    <a
                      href='/settings'
                      className='px-4 py-2 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg'
                    >
                      {t('dashboard.settings')}
                    </a>
                    <button
                      onClick={handleLogout}
                      className='px-4 py-2 rounded-lg bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
                    >
                      {t('nav.signOut')}
                    </button>
                  </>
                ) : (
                  <>
                    <a
                      href='/login'
                      className='px-4 py-2 rounded-lg bg-blue-600 text-white text-center hover:bg-blue-700'
                    >
                      {t('nav.signIn')}
                    </a>
                  </>
                )}
                <div className='flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700 mt-2'>
                  <span className='text-xs uppercase tracking-widest text-gray-500'>
                    {t('nav.language')}
                  </span>
                  <select
                    aria-label={t('nav.language')}
                    value={locale}
                    onChange={e => setLocale(e.target.value as 'en' | 'th' | 'id')}
                    className='bg-transparent text-sm text-gray-700 dark:text-gray-200'
                  >
                    <option value='en'>English</option>
                    <option value='th'>ไทย</option>
                    <option value='id'>Bahasa</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
